import React from 'react';
import { Box, Typography, useTheme, keyframes } from '@mui/material';
import { TypingIndicatorProps } from '../../types/message';

const dotPulse = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * 入力中表示コンポーネント
 * AI応答中のローディングアニメーションを表示
 */
export const TypingIndicator: React.FC<TypingIndicatorProps> = ({
  isVisible,
  expertName,
}) => {
  const theme = useTheme();

  if (!isVisible) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: theme.spacing(2),
        marginBottom: theme.spacing(1),
        animation: `${fadeIn} 0.3s ease-out`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: theme.palette.grey[100],
          borderRadius: theme.shape.borderRadius * 2,
          padding: theme.spacing(1.5, 2),
          maxWidth: '300px',
          border: `1px solid ${theme.palette.grey[200]}`,
          ...(theme.palette.mode === 'dark' && {
            backgroundColor: theme.palette.grey[800],
            borderColor: theme.palette.grey[700],
          }),
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.grey[600],
            marginRight: theme.spacing(1.5),
            fontSize: '0.875rem',
            fontWeight: 500,
            ...(theme.palette.mode === 'dark' && {
              color: theme.palette.grey[400],
            }),
          }}
        >
          {expertName ? `${expertName}が回答を準備中` : 'AIが回答を準備中'}
        </Typography>
        
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
          }}
        >
          {[0, 1, 2].map((index) => (
            <Box
              key={index}
              sx={{
                width: 8,
                height: 8,
                backgroundColor: theme.palette.primary.main,
                borderRadius: '50%',
                animation: `${dotPulse} 1.4s infinite ease-in-out`,
                animationDelay: `${index * 0.16}s`,
                ...(theme.palette.mode === 'dark' && {
                  backgroundColor: theme.palette.primary.light,
                }),
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};